import Vue from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import store from '@/store'
import router from '@/router'
import { getSucursalUuid, getCajaId } from '@/store/modules/sucursales/contexto'

// -------------------------
// BASE URL
// -------------------------

let baseURL = process.env.NODE_ENV === "production"
  ? (process.env.VUE_APP_BACKEND_URL || 'https://api.legorsoft.click/api/v1')
  : `http://${process.env.HOST || '127.0.0.1:8080'}/api/v1`

axios.defaults.baseURL = baseURL
axios.defaults.withCredentials = true

Vue.use(VueAxios, axios)


// -------------------------
// ENDPOINTS
// -------------------------

const TOKEN_URL = '/usuarios/token'
const TOKEN_REFRESH_URL = '/usuarios/token/refresh'
const LOGOUT_URL = '/usuarios/logout'
const EMPRESA_PUBLIC_URL = '/empresas/public'

const PUBLIC_URLS = [
  TOKEN_URL,
  TOKEN_REFRESH_URL,
  LOGOUT_URL,
  EMPRESA_PUBLIC_URL
]


// -------------------------
// TOKENS
// -------------------------

function getTokenAccess() {
  return localStorage.getItem('token_access')
}

function getTokenRefresh() {
  return localStorage.getItem('token_refresh')
}

function saveTokenAccess(token) {
  localStorage.setItem('token_access', token)
}

function saveTokenRefresh(token) {
  localStorage.setItem('token_refresh', token)
}

function clearTokens() {
  localStorage.removeItem('token_access')
  localStorage.removeItem('token_refresh')
}


// -------------------------
// REFRESH TOKEN
// -------------------------

async function refreshAccessToken(refreshToken) {

  const response = await axios.post(TOKEN_REFRESH_URL, {
    refresh: refreshToken
  })

  const { access, refresh } = response.data

  saveTokenAccess(access)

  if (refresh) {
    saveTokenRefresh(refresh)
  }

  return access
}


// -------------------------
// REQUEST QUEUE
// -------------------------

let isRefreshing = false
let requestsQueue = []

function enqueueRequest(request) {
  return new Promise((resolve, reject) => {
    requestsQueue.push({ request, resolve, reject })
  })
}

async function resolvePendingRequests(newToken) {

  while (requestsQueue.length > 0) {

    const { request, resolve, reject } = requestsQueue.shift()

    request.headers.Authorization = `Bearer ${newToken}`

    try {
      const response = await axios(request)
      resolve(response)
    } catch (error) {
      reject(error)
    }
  }
}

function rejectPendingRequests(error) {

  while (requestsQueue.length > 0) {
    const { reject } = requestsQueue.shift()
    reject(error)
  }

}


// -------------------------
// LOGOUT
// -------------------------

function logoutUser(message = 'Su sesión ha expirado') {

  clearTokens()

  store.dispatch('auth/logOut', message)

  router.push({ name: 'login' })

}


// -------------------------
// REQUEST INTERCEPTOR
// -------------------------

axios.interceptors.request.use(

  (config) => {

    if (PUBLIC_URLS.includes(config.url)) {
      return config
    }

    const accessToken = getTokenAccess()

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    // El contexto de operación viaja por header, no por cookie: la cookie
    // era de dominio compartido (.legorsoft.click) y se filtraba entre
    // empresas. localStorage está aislado por subdominio.
    const sucursalUuid = getSucursalUuid()
    if (sucursalUuid) {
      config.headers['X-Sucursal'] = sucursalUuid
    }

    const cajaId = getCajaId()
    if (cajaId) {
      config.headers['X-Caja'] = cajaId
    }

    return config

  },

  (error) => Promise.reject(error)

)


// -------------------------
// RESPONSE INTERCEPTOR
// -------------------------

axios.interceptors.response.use(

  (response) => response,

  async (error) => {

    if (!error.config || !error.response) {
      return Promise.reject(error)
    }

    const originalRequest = error.config

    if (originalRequest.url === TOKEN_REFRESH_URL) {
      logoutUser()
      return Promise.reject(error)
    }

    if (PUBLIC_URLS.includes(originalRequest.url)) {
      return Promise.reject(error)
    }

    if (error.response.status === 401 && !originalRequest.__isRetry) {

      originalRequest.__isRetry = true

      const refreshToken = getTokenRefresh()

      if (!refreshToken) {
        logoutUser()
        return Promise.reject(error)
      }

      if (!isRefreshing) {

        isRefreshing = true

        try {

          const newAccess = await refreshAccessToken(refreshToken)

          await resolvePendingRequests(newAccess)

          originalRequest.headers.Authorization = `Bearer ${newAccess}`

          return axios(originalRequest)

        }

        catch (err) {

          rejectPendingRequests(err)

          logoutUser()

          return Promise.reject(err)

        }

        finally {

          isRefreshing = false

        }

      }

      else {

        return enqueueRequest(originalRequest)

      }

    }

    return Promise.reject(error)

  }

)


export default axios